import React from 'react';

import { useIntl, defineMessages } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

const messages = defineMessages({
	loadMoreError: {
		id: 'conversation-assistant-ui-components.common.chat-conversation-list.conversation-pagination-controls.loadMoreError',
		defaultMessage: 'Failed to load more chats.',
		description:
			'Error message displayed below the chat conversation list when loading the next page of conversations fails.',
	},
	loadMoreRetry: {
		id: 'conversation-assistant-ui-components.common.chat-conversation-list.conversation-pagination-controls.loadMoreRetry',
		defaultMessage: 'Try again',
		description:
			'Button label for retrying the request to load more chats in the chat conversation list.',
	},
	loadingMoreLabel: {
		id: 'conversation-assistant-ui-components.common.chat-conversation-list.conversation-pagination-controls.loadingMoreLabel',
		defaultMessage: 'Loading more chats',
		description:
			'Accessible label for the loading spinner shown while the chat conversation list loads another page.',
	},
});

const styles = cssMap({
	loadMoreSpinner: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '48px',
		overflowAnchor: 'none',
	},
	loadMoreError: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.100'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		overflowAnchor: 'none',
	},
});

type ConversationPaginationControlsProps = {
	as?: 'div' | 'li';
	role?: 'listitem';
	hasMoreConversations?: boolean;
	isLoadingMoreConversations?: boolean;
	hasLoadMoreConversationsError?: boolean;
	onLoadMoreConversations?: () => void;
	testIdPrefix: string;
};

export const ConversationPaginationControls = ({
	as = 'li',
	role,
	hasMoreConversations = false,
	isLoadingMoreConversations = false,
	hasLoadMoreConversationsError = false,
	onLoadMoreConversations,
	testIdPrefix,
}: ConversationPaginationControlsProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [hasPendingRetry, setHasPendingRetry] = React.useState(false);
	const wasLoadingMoreRef = React.useRef(isLoadingMoreConversations);

	React.useEffect(() => {
		if (wasLoadingMoreRef.current && !isLoadingMoreConversations) {
			setHasPendingRetry(false);
		}

		wasLoadingMoreRef.current = isLoadingMoreConversations;
	}, [isLoadingMoreConversations]);

	const handleRetry = React.useCallback(() => {
		if (isLoadingMoreConversations || hasPendingRetry || !onLoadMoreConversations) {
			return;
		}

		setHasPendingRetry(true);
		onLoadMoreConversations();
	}, [hasPendingRetry, isLoadingMoreConversations, onLoadMoreConversations]);

	if (!hasMoreConversations) {
		return null;
	}

	if (hasLoadMoreConversationsError) {
		return (
			<Box
				as={as}
				role={role}
				xcss={styles.loadMoreError}
				testId={`${testIdPrefix}-load-more-error`}
			>
				<Text color="color.text.danger">{formatMessage(messages.loadMoreError)}</Text>
				<Button
					appearance="subtle"
					spacing="compact"
					isDisabled={isLoadingMoreConversations || hasPendingRetry}
					onClick={handleRetry}
				>
					{formatMessage(messages.loadMoreRetry)}
				</Button>
			</Box>
		);
	}

	if (isLoadingMoreConversations) {
		return (
			<Box
				as={as}
				role={role}
				xcss={styles.loadMoreSpinner}
				testId={`${testIdPrefix}-load-more-loading`}
			>
				<Spinner size="small" label={formatMessage(messages.loadingMoreLabel)} />
			</Box>
		);
	}

	return null;
};
